import { useStaleSWR } from "@/hooks/useStaleSWR";
import {
  Alert,
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Link as ChakraLink,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import axios from "axios";
import { format, isAfter, isBefore } from "date-fns";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { HelpCircle } from "react-feather";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import useSWR from "swr";

import BoxCard from "@/components/common/BoxCard";
import EventInformation from "@/components/Events/Application/EventInformation";
import EventDescription from "@/components/Events/EventDescription";
import { HelpChatLink } from "@/consts/help";
import { useToastPromise } from "@/hooks/useToast";
import { mutateSession, useUser } from "@/hooks/useUser";
import { Accreditation } from "@/types/accreditation";
import { AccreditationLocations, AccreditationTypeFields } from "@/types/event";
import { Website } from "@/types/website";
import { getSocialMediaNickname } from "@/utils/socialMedia";
import { warning } from "@/utils/toast";
import getConfig from "next/config";
import EditorialOfficeModal from "../EditorialOffices/EditorialOfficeModal";
import AccreditationFields from "./AccreditationFields";
import AccreditationRegulations from "./AccreditationRegulations";
import AccreditationStatus from "./AccreditationStatus";
import BasicInformations from "./BasicInformations";
import Location from "./Location";
import MySocialMedia from "./MySocialMedia";
import SendRequestDialog from "./SendRequestDialog";

const { publicRuntimeConfig } = getConfig();
const { APP_URL } = publicRuntimeConfig;

type Props = {
  id: string | string[];
  rounded?: boolean;
  website?: Website;
};

type AlertProps = {
  accreditationFrom: string;
  accreditationTo: string;
};

type Inputs = {
  accreditationLocations: AccreditationLocations[];
  accreditation: Accreditation;
  editorialOffice: number;
  firstName: string;
  lastName: string;
  email: string;
  facebook: string;
  twitter: string;
  instagram: string;
  twitch: string;
  youtube: string;
  tiktok: string;
  linkedin: string;
  otherSocial: string;
};

const AccreditationNotActiveAlert = ({
  accreditationFrom,
  accreditationTo,
}: AlertProps) => {
  const { t } = useTranslation("common");
  return (
    <Alert status="info" mt="20px">
      <AlertIcon />
      {t("events.accreditationNotActiveTime", {
        start: format(new Date(accreditationFrom), "dd/MM/yyyy"),
        end: format(new Date(accreditationTo), "dd/MM/yyyy"),
      })}
    </Alert>
  );
};

const Event = ({
  id,
  rounded = true,
  website = null,
}: Props): React.ReactElement => {
  const { t } = useTranslation("common");
  const toast = useToastPromise();
  const chakraToast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();
  const {
    data: applicationData,
    error,
    mutate,
  } = useStaleSWR(`/accreditation/event/${id}`);
  const { data: editorialOffices, mutate: mutateOffices } = useSWR(
    "/accreditation/office/list"
  );

  const router = useRouter();
  const [session] = useUser();

  const [formData, setFormData] = useState();
  const {
    onOpen: invitationSendModalOnOpen,
    isOpen: invitationSendModalIsOpen,
    onClose: invitationSendModalOnClose,
  } = useDisclosure();

  const {
    onOpen: editorialOfficeModalOnOpen,
    isOpen: editorialOfficeModalIsOpen,
    onClose: editorialOfficeModalOnClose,
  } = useDisclosure();
  const [suggestedOfficeName, setSuggestedOfficeName] = useState<string>("");

  const methods = useForm<Inputs>({
    defaultValues: {
      accreditationLocations: [],
      accreditation: {},
      editorialOffice: null,
    },
  });
  const {
    handleSubmit,
    formState: { isSubmitting, dirtyFields },
    control,
    reset,
    watch,
    setValue,
    setError,
  } = methods;

  useEffect(() => {
    reset({
      ...applicationData,
      accreditationLocations:
        applicationData?.event?.accreditationLocations?.map((location) => {
          const choosedType = location.accreditationTypes?.map((type) => {
            return applicationData?.accreditation?.types?.filter(
              (accreditationType) => accreditationType.id === type.id
            );
          });
          return {
            ...location,
            accreditationOptions:
              location?.accreditationOptions?.map((option) => {
                const optionsFromAccreditationFields =
                  applicationData?.accreditation?.fields?.map((field) => {
                    if (field?.accreditationOption) {
                      return {
                        ...field.accreditationOption,
                        accreditationFieldId: field.id,
                        value: field.value,
                      };
                    }
                  });
                const filtredOption = optionsFromAccreditationFields?.filter(
                  (optionFromAccFields) =>
                    optionFromAccFields?.id === option?.id
                );
                if (filtredOption.length === 1) {
                  return {
                    ...filtredOption[0],
                    choosed: true,
                  };
                } else {
                  return {
                    ...option,
                    choosed: false,
                  };
                }
              }) || [],
            choosedType: choosedType.flat(1)[0]?.id || null,
          };
        }),
      editorialOffice:
        applicationData?.accreditation?.editorialOffice?.id || null,
      firstName: applicationData?.accreditation?.firstName || null,
      lastName: applicationData?.accreditation?.lastName || null,
      email: applicationData?.accreditation?.email || null,
      facebook:
        applicationData?.accreditation?.facebook ||
        session?.user?.profile?.facebook,
      twitter:
        applicationData?.accreditation?.twitter ||
        session?.user?.profile?.twitter,
      instagram:
        applicationData?.accreditation?.instagram ||
        session?.user?.profile?.instagram,
      twitch:
        applicationData?.accreditation?.twitch ||
        session?.user?.profile?.twitch,
      youtube:
        applicationData?.accreditation?.youtube ||
        session?.user?.profile?.youtube,
      tiktok:
        applicationData?.accreditation?.tiktok ||
        session?.user?.profile?.tiktok,
      linkedin:
        applicationData?.accreditation?.linkedin ||
        session?.user?.profile?.linkedin,
      otherSocial:
        applicationData?.accreditation?.otherSocial ||
        session?.user?.profile?.otherSocial,
      /* TODO: handle selecting only agreements accepted earlier, now added documents will be selected on edit, too */
      ...(applicationData?.accreditation?.createdAt
        ? applicationData?.event?.accreditationAgreements.reduce(
            (agreements, agreement) => ({
              ...agreements,
              [`agreement_${agreement.id}`]: true,
            }),
            {}
          )
        : {}),
    });
  }, [applicationData]);

  const { fields }: { fields: AccreditationLocations[] } = useFieldArray({
    name: "accreditationLocations",
    control,
  });

  const editorialOffice = watch("editorialOffice");
  const accreditationLocations = watch("accreditationLocations", fields);

  const onSubmit = async (data) => {
    if (!data?.firstName?.trim())
      return setError(
        "firstName",
        { type: "whiteSpacesError", message: t("events.noWhitespacesInfo") },
        { shouldFocus: true }
      );

    if (!data?.lastName?.trim())
      return setError(
        "lastName",
        { type: "whiteSpacesError", message: t("events.noWhitespacesInfo") },
        { shouldFocus: true }
      );

    if (Object.keys(dirtyFields).length > 0) onOpenChangeProfileModal(data);
    else return sendData(data);
  };

  const onOpenChangeProfileModal = (data) => {
    onOpen();
    setFormData(data);
  };

  const sendData = async (data, changeProfile?: boolean) => {
    const facebook = getSocialMediaNickname(data?.facebook);
    const twitter = getSocialMediaNickname(data?.twitter);
    const instagram = getSocialMediaNickname(data?.instagram);
    const youtube = getSocialMediaNickname(data?.youtube);
    const linkedin = getSocialMediaNickname(data?.linkedin);
    const tiktok = getSocialMediaNickname(data?.tiktok);

    if (facebook.error)
      return chakraToast(warning(t("socialMedia.invalidURLFacebook")));
    if (twitter.error)
      return chakraToast(warning(t("socialMedia.invalidURLTwitter")));
    if (instagram.error)
      return chakraToast(warning(t("socialMedia.invalidURLInstagram")));
    if (youtube.error)
      return chakraToast(warning(t("socialMedia.invalidURLYoutube")));
    if (linkedin.error)
      return chakraToast(warning(t("socialMedia.invalidURLLinkedin")));
    if (tiktok.error)
      return chakraToast(warning(t("socialMedia.invalidURLTiktok")));

    const accreditationOptions = data.accreditationLocations
      ?.map((location) => location.accreditationOptions)
      .flat(1)
      .filter((option) => option?.choosed)
      .map((option) => {
        return {
          accreditationOption: option.id,
          id: option.accreditationFieldId,
          value: option.value,
        };
      });

    const accreditationFields = data.accreditation?.fields
      ?.filter((field) => field.accreditationField)
      .map((field) => {
        if (
          field.accreditationField.type === AccreditationTypeFields.CHECKBOX &&
          !field.value
        )
          return field;
        if (
          field.accreditationField.type === AccreditationTypeFields.FILE &&
          field.accreditationField.name.en === "photo" &&
          field.accreditationField.default
        )
          return field;
        if (field.value) return field;
      })
      .filter((field) => field)
      .map((field) => ({
        accreditationField: field.accreditationField.id,
        value: field.value,
        id: typeof field.id === "number" ? field.id : undefined,
      }));

    const dataToSend = {
      fields: [...(accreditationFields || []), ...(accreditationOptions || [])],
      types: data.accreditationLocations?.map((location) => {
        return {
          id: location.choosedType,
        };
      }),
      editorialOffice:
        data.editorialOffice === "freelancer" ? null : data.editorialOffice,
      facebook: facebook?.nick,
      twitter: twitter?.nick,
      instagram: instagram?.nick,
      youtube: youtube?.nick,
      linkedin: linkedin?.nick,
      tiktok: tiktok?.nick,
      otherSocial: data?.otherSocial,
      firstName: data.firstName?.trim(),
      lastName: data.lastName?.trim(),
      email: data.email?.trim(),
    };

    const files = dataToSend.fields
      .filter(
        (field) =>
          field.value instanceof FileList || field.value instanceof File
      )
      .map((file) => ({
        id: file.accreditationField,
        value: file.value,
      }));

    const changeProfileData = new FormData();
    const changeProfilePicture = new FormData();

    if (changeProfile) {
      if (dirtyFields?.firstName) {
        changeProfileData.append("firstName", data.firstName?.trim());
      }

      if (dirtyFields?.lastName) {
        changeProfileData.append("lastName", data.lastName?.trim());
      }

      if (dirtyFields?.email) {
        changeProfileData.append("email", data.email?.trim());
      }

      if (dirtyFields?.facebook) {
        changeProfileData.append("facebook", data.facebook);
      }

      if (dirtyFields?.instagram) {
        changeProfileData.append("instagram", data.instagram);
      }

      if (dirtyFields?.twitch) {
        changeProfileData.append("twitch", data.twitch);
      }

      if (dirtyFields?.twitter) {
        changeProfileData.append("twitter", data.twitter);
      }

      if (dirtyFields?.tiktok) {
        changeProfileData.append("tiktok", data.tiktok);
      }

      if (dirtyFields?.youtube) {
        changeProfileData.append("youtube", data.youtube);
      }

      if (dirtyFields?.linkedin) {
        changeProfileData.append("twitch", data.linkedin);
      }

      if (dirtyFields?.otherSocial) {
        changeProfileData.append("otherSocial", data.otherSocial);
      }

      if (dirtyFields?.accreditation?.fields.length > 0) {
        dirtyFields?.accreditation?.fields
          .map((field) => {
            return data.accreditation?.fields[
              dirtyFields?.accreditation?.fields.indexOf(field)
            ];
          })
          .forEach((field) => {
            if (field.accreditationField.name.en === "address") {
              changeProfileData.append("address", field.value.address || "");
              changeProfileData.append("city", field.value.city || "");
              changeProfileData.append("country", field.value.country || "");
              changeProfileData.append(
                "postalCode",
                field.value.postalCode || ""
              );
            } else if (field.accreditationField.name.en === "photo") {
              changeProfilePicture.append("picture", field.value);
            } else {
              changeProfileData.append(
                field.accreditationField.name.en,
                field.value
              );
            }
          });
      }
    }

    const formData = new FormData();
    if (files.length > 0) {
      files.forEach((file) => {
        if (file?.value instanceof Array || file?.value instanceof FileList)
          [...file.value].forEach((fileFromFileList) => {
            formData.append(file.id, fileFromFileList, fileFromFileList.name);
          });
        if (file?.value instanceof File)
          formData.append(file.id, file.value, file.value.name);
      });
    }

    const changedFormData = !formData.entries().next().done;
    const changedProfileData = !changeProfileData.entries().next().done;
    const changedProfilePicture =
      !session?.user?.profile?.picture &&
      !changeProfilePicture.entries().next().done;

    await toast.promise(
      axios
        .all([
          axios.post(`/accreditation/event/${id}`, dataToSend),
          changedFormData &&
            axios.post(`/accreditation/event/${id}`, formData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }),
          changedProfileData &&
            axios.patch("/user/edit", changeProfileData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }),
          changedProfilePicture &&
            axios.post("/profile/picture", changeProfilePicture, {
              headers: { "Content-Type": "multipart/form-data" },
            }),
        ])
        .then(async () => {
          if (changedProfileData || changedProfilePicture) mutateSession();

          await mutate();
          router.push(
            `${website?.isSubdomain ? APP_URL : ""}/accreditation/applications`
          );
        }),
      {
        errorKeys: {
          AccreditationUpdateNotAllowed: t(
            "errorKeys.accreditationUpdateNotAllowed"
          ),
          SubscriptionRequired: t("errorKeys.subscriptionRequired"),
          EmailIsAlreadyInUse: t("users.emailTaken"),
          UserNotFound: t("errorKeys.userNotFound"),
        },
      }
    );
  };

  const suggestEditorialOffice = useCallback((name: string) => {
    setSuggestedOfficeName(name);
    editorialOfficeModalOnOpen();
  }, []);

  const onCloseSuggestModal = async (id) => {
    setSuggestedOfficeName(null);
    editorialOfficeModalOnClose();

    await mutateOffices();

    if (id) {
      setValue("editorialOffice", id);
    }
  };

  const onSubmitSuggestModal = ({ success, id }) => {
    if (success) {
      onCloseSuggestModal(id);
    }
  };

  const accreditaitonHeader = (
    <>
      <EventInformation eventData={applicationData?.event} rounded={rounded} />
      {(applicationData?.event?.description?.[session?.user?.profile?.locale] ||
        applicationData?.event?.description?.en ||
        applicationData?.event?.description?.[
          Object.keys(applicationData?.event?.description)[0]
        ]) && (
        <BoxCard p={0} mt={3} borderRadius={rounded ? ".375rem" : 0}>
          <Box p="1.25rem" borderBottomColor="light.50" borderBottomWidth="1px">
            <Heading as="h3" size="md">
              {t("events.description")}
            </Heading>
          </Box>
          <Box p="1.25rem">
            <Text textAlign={"justify"}>
              <EventDescription event={applicationData.event} />
            </Text>
          </Box>
        </BoxCard>
      )}
    </>
  );

  const now = new Date();

  const accreditationNotActiveDates = useMemo(() => {
    const accreditationFrom =
      applicationData?.event?.accreditationLocations?.reduce(
        (date, location) =>
          isBefore(location.startAccreditation, date)
            ? location.startAccreditation
            : date,
        applicationData?.event.accreditationLocations?.[0]?.startAccreditation
      );
    const accreditationTo =
      applicationData?.event?.accreditationLocations?.reduce(
        (date, location) =>
          isAfter(location.endAccreditation, date)
            ? location.endAccreditation
            : date,
        applicationData?.event.accreditationLocations?.[0]?.endAccreditation
      );

    return {
      accreditationNotActive: isBefore(now, new Date(accreditationFrom)),
      accreditationFrom,
      accreditationTo,
    };
  }, [applicationData]);

  if (!applicationData && !error)
    return (
      <Center>
        <Spinner />
      </Center>
    );

  if (
    accreditationNotActiveDates?.accreditationNotActive &&
    !(
      (session.currentRole?.role == "admin" ||
        session.currentRole?.role == "mod") &&
      session?.currentRole?.id == applicationData?.event?.pressOffice?.id
    )
  ) {
    return (
      <>
        {accreditaitonHeader}
        <AccreditationNotActiveAlert
          accreditationFrom={accreditationNotActiveDates?.accreditationFrom}
          accreditationTo={accreditationNotActiveDates?.accreditationTo}
        />
      </>
    );
  }

  if (
    error ||
    (!applicationData?.accreditationActive &&
      new Date(accreditationNotActiveDates.accreditationTo) < new Date() &&
      !(
        (session.currentRole.role == "admin" ||
          session.currentRole.role == "mod") &&
        session?.currentRole?.id == applicationData?.event?.pressOffice?.id
      ))
  ) {
    return (
      <>
        {accreditaitonHeader}
        <Alert status="info" mt="20px">
          <AlertIcon />
          {t("events.accreditationNotActive")}
        </Alert>
      </>
    );
  }

  return (
    <>
      {applicationData?.accreditation?.status && (
        <AccreditationStatus
          status={applicationData?.accreditation?.status}
          mb={4}
        />
      )}
      {accreditaitonHeader}
      {!applicationData?.accreditationActive &&
        new Date(accreditationNotActiveDates.accreditationTo) < new Date() && (
          <Alert status="info" mt="20px">
            <AlertIcon />
            {t("events.accreditationNotActive")}
          </Alert>
        )}
      {accreditationNotActiveDates?.accreditationNotActive && (
        <AccreditationNotActiveAlert
          accreditationFrom={accreditationNotActiveDates?.accreditationFrom}
          accreditationTo={accreditationNotActiveDates?.accreditationTo}
        />
      )}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <BasicInformations
            editorialOffices={editorialOffices}
            mutateData={mutateOffices}
            invitationModalOpen={invitationSendModalOnOpen}
            suggestEditorialOffice={suggestEditorialOffice}
            rounded={rounded}
          />
          <MySocialMedia />
          <AccreditationFields rounded={rounded} />
          {applicationData?.event?.accreditationAgreements?.length > 0 && (
            <AccreditationRegulations rounded={rounded} />
          )}
          {accreditationLocations?.length > 0 && (
            <Box mt={5}>
              {accreditationLocations?.map((location, index) => (
                <Location
                  rounded={rounded}
                  key={index}
                  location={location}
                  index={index}
                />
              ))}
            </Box>
          )}
          <Box
            position={{ base: "unset", md: "sticky" }}
            bottom={0}
            bg="light.100"
            pb={{ base: 0, md: "1.25rem" }}
            mb={{ base: 0, md: "-1.25rem" }}
          >
            <Flex
              justifyContent={{ base: undefined, md: "space-between" }}
              flexDirection={{ base: "column", md: "row" }}
            >
              <Button isLoading={isSubmitting} type="submit" mt="1.25rem">
                {t("save")}
              </Button>
              <ChakraLink isExternal href={HelpChatLink}>
                <Button mt="1.25rem" leftIcon={<HelpCircle />}>
                  {t("help.help")}
                </Button>
              </ChakraLink>
            </Flex>
          </Box>
        </form>
        <SendRequestDialog
          id={editorialOffice}
          isOpen={invitationSendModalIsOpen}
          onClose={invitationSendModalOnClose}
          mutateData={mutateOffices}
        />
      </FormProvider>
      <AlertDialog
        motionPreset="slideInBottom"
        onClose={onClose}
        isOpen={isOpen}
        isCentered
        leastDestructiveRef={cancelRef}
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader>
            {t("events.accreditationFieldChangeProfile")}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              isLoading={isSubmitting}
              variant="outline"
              onClick={() => {
                onClose();
                sendData(formData, false);
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              ml={3}
              isLoading={isSubmitting}
              onClick={() => {
                onClose();
                sendData(formData, true);
              }}
            >
              {t("save")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <EditorialOfficeModal
        defaultValues={{ name: suggestedOfficeName }}
        isOpen={editorialOfficeModalIsOpen}
        onClose={onCloseSuggestModal}
        onSubmit={onSubmitSuggestModal}
        withRequest
      />
    </>
  );
};

export default Event;
